import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Col, Form, Input, InputNumber, Row, Select, Typography, message } from 'antd';
import api from '../../services/api';
import { amountToChinese } from '../../utils/moneyChinese';
import './ExpenseClaimForm.css';

const { Text } = Typography;

const COL3 = 8;
const FULL = 24;
const GUTTER = [16, 8];

function FieldCol({ span = COL3, children }) {
  return <Col xs={24} md={span}>{children}</Col>;
}

function FormSection({ title, children }) {
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{title}</div>
      {children}
    </section>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <Form.Item label={label}>
      <Text>{value || '—'}</Text>
    </Form.Item>
  );
}

export default function InternalFundTransferForm({ form, initialValues, onValuesChange }) {
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [accountOptions, setAccountOptions] = useState([]);
  const [fundNatureOptions, setFundNatureOptions] = useState([]);

  const transferAmount = Form.useWatch('transfer_amount', form);
  const outAccountId = Form.useWatch('out_account_id', form);
  const inAccountId = Form.useWatch('in_account_id', form);

  // 退回重提场景：initialValues 由父组件按流程记录缓存，仅在该记录切换时变化，
  // 因此这里用它的引用变化来触发回填，既保证初始回填又不会覆盖用户的后续编辑。
  useLayoutEffect(() => {
    if (!initialValues || !Object.keys(initialValues).length) return;
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const accountMap = useMemo(
    () => Object.fromEntries(accountOptions.map((item) => [item.id, item])),
    [accountOptions]
  );

  const accountSelectOptions = useMemo(
    () => accountOptions.map((item) => ({ value: item.id, label: item.label })),
    [accountOptions]
  );

  const syncAccountFields = useCallback((prefix, accountId) => {
    const account = accountMap[accountId];
    form.setFieldsValue({
      [`${prefix}_bank_account_no`]: account?.bank_account_no || undefined,
      [`${prefix}_bank_branch_name`]: account?.bank_branch_name || undefined
    });
  }, [accountMap, form]);

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/expense/fin-internal-fund-transfer/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setAccountOptions(data.account_options || []);
      setFundNatureOptions(data.fund_nature_options || []);
    } catch (e) {
      setAccountOptions([]);
      setFundNatureOptions([]);
      message.error(e.response?.data?.message || '加载内部资金调拨表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (outAccountId && accountMap[outAccountId]) {
      syncAccountFields('out', outAccountId);
    }
  }, [outAccountId, accountMap, syncAccountFields]);

  useEffect(() => {
    if (inAccountId && accountMap[inAccountId]) {
      syncAccountFields('in', inAccountId);
    }
  }, [inAccountId, accountMap, syncAccountFields]);

  const outBankAccountNo = Form.useWatch('out_bank_account_no', form);
  const outBankBranchName = Form.useWatch('out_bank_branch_name', form);
  const inBankAccountNo = Form.useWatch('in_bank_account_no', form);
  const inBankBranchName = Form.useWatch('in_bank_branch_name', form);

  const handleOutAccountChange = (accountId) => {
    form.setFieldsValue({ out_account_id: accountId });
    syncAccountFields('out', accountId);
  };

  const handleInAccountChange = (accountId) => {
    form.setFieldsValue({ in_account_id: accountId });
    syncAccountFields('in', accountId);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="expense-claim-form"
      style={{ opacity: loadingMeta ? 0.6 : 1 }}
      onValuesChange={onValuesChange}
    >
      <Form.Item name="out_bank_account_no" hidden><Input /></Form.Item>
      <Form.Item name="out_bank_branch_name" hidden><Input /></Form.Item>
      <Form.Item name="in_bank_account_no" hidden><Input /></Form.Item>
      <Form.Item name="in_bank_branch_name" hidden><Input /></Form.Item>

      <FormSection title="基本信息">
        <Row gutter={GUTTER}>
          <FieldCol>
            <Form.Item
              name="transfer_amount"
              label="划拨金额"
              rules={[{ required: true, message: '请填写划拨金额' }]}
            >
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} addonBefore="¥" />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <ReadonlyField label="大写金额" value={amountToChinese(transferAmount)} />
          </FieldCol>
          <FieldCol>
            <Form.Item
              name="fund_nature"
              label="资金性质"
              rules={[{ required: true, message: '请选择资金性质' }]}
            >
              <Select
                placeholder="请选择资金性质"
                loading={loadingMeta}
                options={fundNatureOptions}
              />
            </Form.Item>
          </FieldCol>
          <FieldCol span={FULL}>
            <Form.Item
              name="purpose"
              label="划拨事由及用途说明"
              rules={[{ required: true, message: '请填写划拨事由及用途说明' }]}
            >
              <Input.TextArea rows={3} maxLength={500} showCount placeholder="请说明划拨事由及用途" />
            </Form.Item>
          </FieldCol>
        </Row>
      </FormSection>

      <FormSection title="资金调出单位信息">
        <Row gutter={GUTTER}>
          <FieldCol>
            <Form.Item
              name="out_account_id"
              label="资金调出单位名称"
              rules={[{ required: true, message: '请选择调出单位' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="请选择调出单位"
                loading={loadingMeta}
                optionFilterProp="label"
                options={accountSelectOptions}
                onChange={handleOutAccountChange}
              />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <ReadonlyField label="账号" value={outBankAccountNo || accountMap[outAccountId]?.bank_account_no} />
          </FieldCol>
          <FieldCol>
            <ReadonlyField label="开户行" value={outBankBranchName || accountMap[outAccountId]?.bank_branch_name} />
          </FieldCol>
        </Row>
      </FormSection>

      <FormSection title="资金调入单位信息">
        <Row gutter={GUTTER}>
          <FieldCol>
            <Form.Item
              name="in_account_id"
              label="资金调入单位名称"
              rules={[{ required: true, message: '请选择调入单位' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="请选择调入单位"
                loading={loadingMeta}
                optionFilterProp="label"
                options={accountSelectOptions}
                onChange={handleInAccountChange}
              />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <ReadonlyField label="账号" value={inBankAccountNo || accountMap[inAccountId]?.bank_account_no} />
          </FieldCol>
          <FieldCol>
            <ReadonlyField label="开户行" value={inBankBranchName || accountMap[inAccountId]?.bank_branch_name} />
          </FieldCol>
        </Row>
      </FormSection>
    </Form>
  );
}
